// frontend/src/pages/VerifyEmailPage.tsx
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Loader, CheckCircle, XCircle } from 'lucide-react';
import api from '../services/api';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link.');
        return;
      }
      try {
        const res = await api.get(`/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(res.data.message || 'Email verified successfully!');
      } catch (err: any) {
        setStatus('error');
        setMessage(err?.response?.data?.message || 'Invalid or expired verification link.');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 via-white to-romcom-blue/10 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md text-center"
      >
        <Link to="/" className="inline-flex flex-col items-center gap-2 mb-8">
          <Heart className="w-12 h-12 text-romcom-red fill-current" />
          <div>
            <p className="text-2xl font-playfair font-bold text-romcom-red">ROMCOMTHIS</p>
            <h1 className="text-xs font-semibold tracking-widest text-gray-400 uppercase">For the Love of RomComs</h1>
          </div>
        </Link>

        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader className="w-10 h-10 text-romcom-red animate-spin" />
              <p className="text-gray-500">Verifying your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <CheckCircle className="w-14 h-14 text-green-500" />
              <h2 className="text-2xl font-playfair font-bold text-gray-800">You're in! 💕</h2>
              <p className="text-gray-500 text-sm">{message}</p>
              <Link
                to="/login"
                className="mt-2 bg-gradient-to-r from-romcom-red to-pink-500 text-white px-8 py-3 rounded-xl font-semibold text-sm hover:opacity-90 transition-all shadow-lg"
              >
                Sign In
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <XCircle className="w-14 h-14 text-red-400" />
              <h2 className="text-2xl font-playfair font-bold text-gray-800">Oops! 😕</h2>
              <p className="text-gray-500 text-sm">{message}</p>
              <Link
                to="/login"
                className="mt-2 text-sm text-romcom-red font-semibold hover:underline"
              >
                Back to Sign In
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;